import { useCallback, useEffect, useState, useMemo } from "react";
import { toast } from "react-hot-toast";
import useApi from "../useApi";

export interface IImpactOfLivingQuestion {
  questionText: string;
  impact: string;
  impactAfterChange?: number;
  category?: string;
}

export interface IImpactOfLivingData {
  questionData: IImpactOfLivingQuestion[];
  averagePercentage: number;
}

interface IImpactOfLivingResponse {
  msg: string;
  data: IImpactOfLivingData[];
  status: string;
}

const useImpactOfLivingReport = (billingCycleId: string) => {
  const [impactData, setImpactData] = useState<IImpactOfLivingData | null>(
    null
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const { makeApiCall } = useApi({
    url: `/rtm/patient-dashboard/pdf/v3/impact-of-living/${billingCycleId}`,
    method: "GET",
  });

  const transformImpactData = useCallback(
    (data: IImpactOfLivingData[]): IImpactOfLivingData | null => {
      if (!data || data.length === 0) return null;

      const transformedData = data[0];

      // Transform the question data to ensure it has the required fields
      const transformedQuestions = transformedData.questionData.map(
        (question) => ({
          questionText: question.questionText,
          impact: question.impactAfterChange?.toString() || question.impact,
          impactAfterChange: question.impactAfterChange,
          category: question.category,
        })
      );

      return {
        questionData: transformedQuestions,
        averagePercentage: transformedData.averagePercentage,
      };
    },
    []
  );

  const fetchImpactOfLivingData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { response, error: apiError } = await makeApiCall<
        undefined,
        IImpactOfLivingResponse
      >();

      if (apiError) {
        throw new Error(
          apiError.msg || "Failed to fetch impact of living data"
        );
      }

      if (response?.status === "fail") {
        toast.error(response.msg);
        setImpactData(null);
        setError(response.msg);
        return null;
      }

      if (response?.status === "success" && response.data?.[0]) {
        const transformedData = transformImpactData(response.data);
        setImpactData(transformedData);
        return transformedData;
      } else {
        throw new Error(response?.msg || "No data available");
      }
    } catch (error: any) {
      console.error("Error in fetchImpactOfLivingData:", error);
      const errorMessage =
        error.response?.data?.msg ||
        error.message ||
        "Unable to load impact of living data";
      setError(errorMessage);
      // toast.error(errorMessage);
      setImpactData(null);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [billingCycleId, makeApiCall, transformImpactData]);

  useEffect(() => {
    if (billingCycleId) {
      fetchImpactOfLivingData();
    }
  }, [billingCycleId, fetchImpactOfLivingData]);

  // Memoize the returned data to prevent unnecessary re-renders
  const memoizedData = useMemo(
    () => ({
      impactData,
      isLoading,
      error,
      refetch: fetchImpactOfLivingData,
    }),
    [impactData, isLoading, error, fetchImpactOfLivingData]
  );

  return memoizedData;
};

export default useImpactOfLivingReport;
